import { useModal } from "@/app/shared/modal-views/use-modal";
import ReferToSelect from "@/app/shared/refer-to-select";
import { httpService } from "@/Core/Client/AxiosConfig";
import { DATE_FORMAT_OPTIONS, formatDate } from "@/Core/Helpers/DayjsHelpers";
import { Form } from "@/ui/form";
import cn from "@/utils/class-names";
import { IsNullOrWhiteSpace } from "@/utils/Utils";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useState } from "react";
import {
    ArrayPath,
    FieldValues,
    Path,
    SubmitHandler,
    useFieldArray,
} from "react-hook-form";
import { PiPlusBold, PiXBold } from "react-icons/pi";
import { ActionIcon, Button, Flex, Title } from "rizzui";
import TrashIcon from "../icons/trash";
import { Config } from "./crud-config";
import FieldElements from "./field-element";

interface Props<
    R extends Record<string, any>,
    TFormValues extends FieldValues,
> {
    editData?: R;
    config: Config<R, TFormValues>;
}

export default function FormElements<
    R extends Record<string, any>,
    F extends FieldValues,
>({ editData, config }: Props<R, F>) {
    const { closeModal } = useModal();
    const queryClient = useQueryClient();
    const [message, setMessage] = useState<string>();
    // const [reset, setReset] = useState({});

    const [isLoading, setLoading] = useState(false);

    const isUpdate = editData?.id !== undefined;

    const formatNestedData = (data: any, fields: any[]): any => {
        return fields.reduce((acc, f) => {
            if (f.type === "date" || f.type === "datetime") {
                acc[f.name as any] = data[f.name as any]
                    ? formatDate({
                        value: data[f.name as any],
                        format: f.dateFormat || DATE_FORMAT_OPTIONS.LLD,
                    })
                    : data[f.name as any];
            } else if (
                f.type === "array" &&
                Array.isArray(data[f.name as any])
            ) {
                acc[f.name as any] = data[f.name as any].map((item: any) =>
                    formatNestedData(item, f.arrayFields || []),
                );
            } else {
                acc[f.name as any] = data[f.name as any];
            }
            return acc;
        }, {});
    };

    const onSubmit: SubmitHandler<F> = async (data) => {
        setLoading(true);
        console.log("Sending", data);

        try {
            const formattedData = {
                id: data.id,
                ...formatNestedData(data, config.fields!),
            };

            const formData = new FormData();

            const appendFormData = (data: any, parentKey?: string) => {
                // if (data && Array.isArray(data)) {
                //     data.forEach((value) => {
                //         appendFormData(value, parentKey);
                //     });
                // } else
                if (
                    data &&
                    typeof data === "object" &&
                    !(data instanceof Date) &&
                    !(data instanceof File)
                ) {
                    Object.keys(data).forEach((key) => {
                        appendFormData(
                            data[key],
                            parentKey ? `${parentKey}[${key}]` : key,
                        );
                    });
                } else {
                    const value = data == null ? "" : data;
                    formData.append(parentKey!, value);
                }
            };

            if (config.formOptions?.sendAsFormData) {
                // Object.entries(formattedData).forEach(([key, value]) => {
                //     formData.append(
                //         key,
                //         value === undefined ? "" : (value as any),
                //     );
                // });

                appendFormData(formattedData);
            }

            if (!isUpdate) {
                await httpService(config.endPoint).post(
                    config.formOptions?.sendAsFormData
                        ? formData
                        : formattedData,
                );
            } else {
                await httpService(config.endPoint).update(
                    editData.id!,
                    config.formOptions?.sendAsFormData
                        ? formData
                        : formattedData,
                );
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const { response } = error;
                if (response?.data?.message)
                    setMessage(response?.data?.message);
            }
            setLoading(false);
            return;
        }

        // Invalidate and refetch
        queryClient.refetchQueries({ queryKey: config.queryKey });

        setLoading(false);
        closeModal();
        // setReset;
    };

    // has error: the maximum update depth exceeded
    // useEffect(() => {
    //     setReset({ ...editData });
    // }, [editData]);

    return (
        <div className="m-auto p-4 md:px-5 md:py-6">
            <div className="mb-6 flex items-center justify-between">
                <Title as="h3" className="text-lg">
                    {isUpdate
                        ? (config.titles?.pageEditTitle ?? "")
                        : (config.titles?.pageCreateTitle ?? "")}
                </Title>
                <ActionIcon
                    size="sm"
                    variant="text"
                    onClick={() => closeModal()}
                    className="p-0 text-gray-500 hover:!text-gray-900"
                >
                    <PiXBold className="h-[18px] w-[18px]" />
                </ActionIcon>
            </div>

            <Form<F>
                validationSchema={config.validationSchema}
                onSubmit={onSubmit}
                // resetValues={reset} // fixing for the maximum update depth exceeded
                useFormProps={{
                    mode: "all",
                    defaultValues: { ...(editData as any) },
                }}
                className={cn(
                    "grid grid-cols-1 gap-5 @container md:grid-cols-2 [&_label]:font-medium",
                    config.formOptions?.formClassName,
                )}
            >
                {({
                    register,
                    setValue,
                    control,
                    watch,
                    formState: { errors },
                }) => {
                    if (import.meta.env.MODE == "development")
                        console.log("Errors: ", errors, "\n Watch: ", watch());

                    // map the fields
                    const watchedValues = watch();
                    return (
                        <>
                            <input
                                type="hidden"
                                {...register("id" as Path<F>)}
                                value={editData?.id ?? ""}
                            />

                            {config.fields
                                ?.filter((a) => !a.filterOnly)
                                .map((field, n) => {
                                    // hide the field if it depends on another field and the value is empty
                                    if (
                                        field.dependsOn &&
                                        field.dependsOn.filter
                                    ) {
                                        if (
                                            IsNullOrWhiteSpace(
                                                watchedValues[
                                                field.dependsOn.name
                                                ],
                                            )
                                        ) {
                                            return <></>;
                                        }
                                    }

                                    // if the field is an array
                                    if (field.type === "array") {
                                        const { fields, append, remove } =
                                            useFieldArray({
                                                control,
                                                name: field.name as ArrayPath<F>,
                                            });

                                        if (fields.length === 0)
                                            append({} as any);

                                        return (
                                            <>
                                                {fields?.map((item, index) => (
                                                    <Flex
                                                        key={item.id}
                                                        justify="start"
                                                        className={cn(
                                                            "col-span-full flex gap-4 xl:gap-7",
                                                            // "col-span-full grid grid-cols-1 pt-2",
                                                            `md:grid-cols-${(field.arrayFields?.length ?? 0) + 1}`,
                                                        )}
                                                    >
                                                        {field.arrayFields?.map(
                                                            (e) => {
                                                                if (
                                                                    e.type ===
                                                                    "hidden"
                                                                )
                                                                    return (
                                                                        <input
                                                                            type="hidden"
                                                                            // name={`${field.name as Path<F>}.${index}.${e.name}`}
                                                                            {...register(
                                                                                `${field.name as Path<F>}.${index}.${e.name as Path<F>}` as Path<F>,
                                                                            )}
                                                                        />
                                                                    );

                                                                if (
                                                                    e.type ==
                                                                    "referTo"
                                                                )
                                                                    return (
                                                                        <ReferToSelect
                                                                            title={e.label}
                                                                            name={`${field.name as Path<F>}.${index}.${e.name as Path<F>}`}
                                                                            setValue={setValue}
                                                                            defaultValue={editData?.[field.name as Path<F>]?.[index]?.[e.name]}
                                                                            className={e.className}
                                                                            error={errors?.[field.name]?.[index]?.[e.name]?.message ?? message}
                                                                            referToEndPoint={e.referEndPoint ?? ""}
                                                                        />
                                                                    );

                                                                return (
                                                                    <FieldElements
                                                                        field={
                                                                            e
                                                                        }
                                                                        control={
                                                                            control
                                                                        }
                                                                        minWidth={
                                                                            e?.minWidth
                                                                        }
                                                                        fieldType={
                                                                            e.type
                                                                        }
                                                                        options={
                                                                            e?.options ??
                                                                            []
                                                                        }
                                                                        rerender={
                                                                            e?.element
                                                                        }
                                                                        label={
                                                                            e.label
                                                                        }
                                                                        placeholder={e?.name
                                                                            ?.toString()
                                                                            ?.capitalize()}
                                                                        className={cn(
                                                                            // "col-span-full",
                                                                            e?.className,
                                                                        )}
                                                                        error={
                                                                            errors?.[
                                                                                e
                                                                                    .name
                                                                            ]
                                                                                ?.message ??
                                                                            message
                                                                        }
                                                                        {...e.props}
                                                                        // field.name as Path<F>,
                                                                        register={register(
                                                                            `${field.name as Path<F>}.${index}.${e.name as Path<F>}` as Path<F>,
                                                                        )}
                                                                    />
                                                                );
                                                            },
                                                        )}

                                                        {fields.length > 1 && (
                                                            <ActionIcon
                                                                onClick={() =>
                                                                    remove(
                                                                        index,
                                                                    )
                                                                }
                                                                variant="flat"
                                                                className="mt-7 shrink-0"
                                                            >
                                                                <TrashIcon className="h-4 w-4" />
                                                            </ActionIcon>
                                                        )}
                                                    </Flex>
                                                ))}
                                                <Button
                                                    type="button"
                                                    onClick={() =>
                                                        append([{} as any])
                                                    }
                                                    variant="outline"
                                                    className="col-span-full ml-auto w-auto"
                                                >
                                                    <PiPlusBold className="me-2 h-4 w-4" />{" "}
                                                    Add
                                                </Button>
                                            </>
                                        );
                                    }

                                    // if the field is a referTo
                                    if (field.type === "referTo") {
                                        return (
                                            <ReferToSelect
                                                key={n}
                                                title={field.label}
                                                name={field.name as string}
                                                register={register}
                                                setValue={setValue}
                                                defaultValue={
                                                    editData?.[
                                                    field.name as any
                                                    ] ?? ""
                                                }
                                                className={field.className}
                                                error={
                                                    errors?.[field.name]
                                                        ?.message ?? message
                                                }
                                                filter={(e) =>
                                                    field.dependsOn
                                                        ? field.dependsOn?.filter?.(
                                                            e,
                                                            watchedValues[
                                                            field
                                                                .dependsOn
                                                                .name
                                                            ],
                                                        )
                                                        : true
                                                }
                                                referToEndPoint={
                                                    field?.referEndPoint ?? ""
                                                }
                                                {...field.props}
                                            />
                                        );
                                    }

                                    // if the field is a referToMultiple
                                    if (field.type === "referToMultiple") {
                                        return (
                                            <ReferToSelect
                                                key={n}
                                                multiple
                                                title={field.label}
                                                name={field.name as string}
                                                register={register}
                                                setValue={setValue}
                                                defaultValue={[
                                                    ...(editData?.[
                                                        field.name as any
                                                    ] ?? ""),
                                                ]}
                                                className={field.className}
                                                filter={field.dependsOn?.filter}
                                                error={
                                                    errors?.[field.name]
                                                        ?.message ?? message
                                                }
                                                referToEndPoint={
                                                    field?.referEndPoint ?? ""
                                                }
                                                {...field.props}
                                            />
                                        );
                                    }

                                    return (
                                        <FieldElements
                                            key={n}
                                            control={control}
                                            field={field}
                                            minWidth={field?.minWidth}
                                            fieldType={field.type}
                                            options={field?.options ?? []}
                                            rerender={field?.element}
                                            label={field.label}
                                            placeholder={`Enter ${field?.label}`}
                                            className={cn(
                                                // "col-span-full",
                                                field?.className,
                                            )}
                                            error={
                                                errors?.[field.name]?.message ??
                                                message
                                            }
                                            // defaultValue={
                                            //     editData?.[
                                            //         field.name as Path<F>
                                            //     ] ?? ""
                                            // }
                                            // placeholder={field?.name?.toString() ?.capitalize()}
                                            // fieldValue={
                                            //     field?.type === "select"
                                            //         ? (field?.options?.find(
                                            //               (a) => a.value === (field.name! as string), ) ?? undefined)
                                            //         : (f.name as string)
                                            // }
                                            // onChange={(e) => {
                                            //     if (f.type === "select") {
                                            //         applyFilter(
                                            //             `${f.name! as string}.value`,
                                            //             e.value,
                                            //             f.filter?.operation,
                                            //         );
                                            //     } else if (f.type === "date") {
                                            //         applyFilter(
                                            //             `${f.name! as string}.value`,
                                            //             formatDate({
                                            //                 value: e,
                                            //                 format: f.dateFormat,
                                            //             }),
                                            //             // f.filter?.operation,
                                            //         );
                                            //         // applyFilter(
                                            //         //     `${f.name! as string}.operation`,
                                            //         //     Operation.in,
                                            //         // );
                                            //     } else {
                                            //         applyFilter(
                                            //             `${f.name! as string}.value`,
                                            //             e.target.value,
                                            //             f.filter?.operation,
                                            //         );
                                            //     }
                                            // }}
                                            // {...field.props}
                                            register={register(
                                                field.name as Path<F>,
                                                {
                                                    valueAsNumber:
                                                        field.type === "number",
                                                },
                                            )}
                                        />
                                    );
                                })}

                            <div
                                className={cn(
                                    "col-span-full grid grid-cols-2 gap-4 pt-5",
                                )}
                            >
                                <Button
                                    variant="outline"
                                    className="w-full @xl:w-auto dark:hover:border-gray-400"
                                    isLoading={isLoading}
                                    onClick={closeModal}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    isLoading={isLoading}
                                    type="submit"
                                    className="hover:gray-700 w-full @xl:w-auto"
                                >
                                    Save
                                </Button>
                            </div>
                        </>
                    );
                }}
            </Form>
        </div>
    );
}
